interface MedicineDictionaryModelVo {
  id: string;
  publicId: string;
  basicInfo: {
    name: string;
    simplifiedName: string;
    pharmaceuticalEffectLiteracy: string;
    medicineImage?: string;
    medicinePictogramImages?: string[];
  };
  classificationInfo?: {
    ethicalType: string;
  };
  companyInfo?: {
    companyName: string;
  };
  ingredientInfo?: {
    formattedIngredientName?: string;
  };
  medicineGuide?: {
    indications?: Array<string>;
    dosages?: Array<string>;
    dosageHtml?: string;
    sideEffects?: Array<string>;
    cautions?: Array<string>;
    interactionHtml?: string;
    storages?: Array<string>;
    videoLink?: string;
    wholeSummary?: string;
  };
  packagingInfo?: {
    image?: string;
  };
}

const DIET_MEDICINE_KEYWORDS = ['삭센다', '마운자로', '위고비'];

class MedicineDictionaryModel {
  private id: string;
  private publicId: string;
  private basicInfo: {
    name: string;
    simplifiedName: string;
    pharmaceuticalEffectLiteracy: string;
    medicineImage?: string;
    medicinePictogramImages?: string[];
  };
  private classificationInfo?: {
    ethicalType: string;
  };
  private companyInfo?: {
    companyName: string;
  };
  private ingredientInfo?: {
    formattedIngredientName?: string;
  };
  private medicineGuide?: {
    indications?: Array<string>;
    dosages?: Array<string>;
    dosageHtml?: string;
    sideEffects?: Array<string>;
    cautions?: Array<string>;
    interactionHtml?: string;
    storages?: Array<string>;
    videoLink?: string;
    wholeSummary?: string;
  };
  private packagingInfo?: {
    image?: string;
  };

  constructor(data: MedicineDictionaryModelVo) {
    this.id = data.id;
    this.publicId = data.publicId;
    this.basicInfo = data.basicInfo;
    this.classificationInfo = data.classificationInfo;
    this.companyInfo = data.companyInfo;
    this.ingredientInfo = data.ingredientInfo;
    this.medicineGuide = data.medicineGuide;
    this.packagingInfo = data.packagingInfo;
  }

  getPublicId = (): string => {
    return this.publicId;
  };

  getMedicinePictogramImages = (): Array<string> => {
    return this.basicInfo.medicinePictogramImages ?? [];
  };

  getSimplifiedName = (): string => {
    return this.basicInfo.simplifiedName;
  };

  getPharmaceuticalEffectLiteracy = (): string => {
    return this.basicInfo.pharmaceuticalEffectLiteracy;
  };

  getIndications = (): Array<string> => {
    return this.medicineGuide?.indications ?? [];
  };

  getDosages = (): Array<string> => {
    return this.medicineGuide?.dosages ?? [];
  };

  getDosageHtml = (): string | undefined => {
    return this.medicineGuide?.dosageHtml;
  };

  getSideEffects = (): Array<string> => {
    return this.medicineGuide?.sideEffects ?? [];
  };

  getEthicalType = (): string | undefined => {
    return this.classificationInfo?.ethicalType;
  };

  getCompanyName = (): string | undefined => {
    return this.companyInfo?.companyName;
  };

  getFormattedIngredientName = (): string | undefined => {
    return this.ingredientInfo?.formattedIngredientName;
  };

  getStorages = (): Array<string> => {
    return this.medicineGuide?.storages ?? [];
  };

  getCaution = (): Array<string> => {
    return this.medicineGuide?.cautions ?? [];
  };

  getInteractionHtml = (): string | undefined => {
    return this.medicineGuide?.interactionHtml;
  };

  getVideoLink = (): string | undefined => {
    return this.medicineGuide?.videoLink;
  };

  getBasicInfoName = (): string => {
    return this.basicInfo.name;
  };

  getWholeSummary = (): string | undefined => {
    return this.medicineGuide?.wholeSummary;
  };

  getRepresentativeImage = (): string | undefined => {
    return this.basicInfo.medicineImage ?? this.packagingInfo?.image ?? undefined;
  };

  getMedicineGuideIndications = (): Array<string> => {
    return this.medicineGuide?.indications ?? [];
  };

  getMedicineGuideDosages = (): Array<string> => {
    return this.medicineGuide?.dosages ?? [];
  };

  getMedicineGuideSideEffects = (): Array<string> => {
    return this.medicineGuide?.sideEffects ?? [];
  };

  getMedicineGuideCautions = (): Array<string> => {
    return this.medicineGuide?.cautions ?? [];
  };

  getMedicineGuideStorages = (): Array<string> => {
    return this.medicineGuide?.storages ?? [];
  };

  getMedicineGuideInteractionHtml = (): string | undefined => {
    return this.medicineGuide?.interactionHtml;
  };

  isDietMedicine = (): boolean => {
    return DIET_MEDICINE_KEYWORDS.includes(this.getSimplifiedName());
  };
}

export default MedicineDictionaryModel;
